/* SANA — Pastilles couleur des cartes produits.
   Survol (souris) ou appui (tactile) d'une pastille : la carte prend la photo,
   la 2e photo et le lien de la fiche de cette couleur.
   Pour que le changement soit instantané :
   - les données des couleurs (/products/{handle}.js) sont chargées dès que la carte approche de l'écran ;
   - les photos sont préchargées dès que la souris entre sur la carte (ou au premier toucher). */
(() => {
  if (customElements.get('sana-card-swatches')) return;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const data = new Map();   // handle -> Promise<{url, shots}>
  const images = new Map(); // url -> Promise<void> (photo chargée et décodée)
  const LADDER = [360, 533, 720, 940, 1066, 1500];

  const sized = (src, w) => {
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  };

  const load = (handle) => {
    if (!data.has(handle)) {
      data.set(handle, fetch(`${root}products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (!p) return null;
          const shots = (p.media || []).filter((m) => m.media_type === 'image').map((m) => m.src);
          if (!shots.length && p.featured_image) shots.push(p.featured_image);
          return { url: p.url, shots };
        })
        .catch(() => null));
    }
    return data.get(handle);
  };

  const preloadImage = (url) => {
    if (!images.has(url)) {
      const img = new Image();
      images.set(url, new Promise((res) => {
        img.onload = res;
        img.onerror = res;
        img.src = url;
        if (img.complete) res();
      }));
    }
    return images.get(url);
  };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          e.target.prefetchData();
        });
      }, { rootMargin: '300px' })
    : null;

  class SanaCardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.card-wrapper');
      if (!this.card) return;
      this.dots = [...this.querySelectorAll('.sana-cs__dot')];
      this.token = 0;

      if (io) io.observe(this); else this.prefetchData();
      const warm = () => this.prefetchImages();
      this.card.addEventListener('pointerenter', warm, { once: true });
      this.card.addEventListener('touchstart', warm, { once: true, passive: true });

      const hover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.dots.forEach((dot) => {
        if (hover) {
          dot.addEventListener('mouseenter', () => this.select(dot));
          dot.addEventListener('focus', () => this.select(dot));
          dot.addEventListener('click', async () => {
            const p = await load(dot.dataset.handle);
            if (p) window.location.href = p.url;
          });
        } else {
          dot.addEventListener('click', (e) => { e.preventDefault(); this.select(dot); });
        }
      });
    }

    // Largeur de photo à demander : celle affichée × densité de l'écran, arrondie au palier supérieur.
    width() {
      const img = this.card.querySelector('.card__media img');
      const need = ((img && img.clientWidth) || 400) * (window.devicePixelRatio || 1);
      return LADDER.find((w) => w >= need) || LADDER[LADDER.length - 1];
    }

    prefetchData() {
      this.dots.forEach((d) => load(d.dataset.handle));
    }

    prefetchImages() {
      const w = this.width();
      this.dots.forEach(async (d) => {
        const p = await load(d.dataset.handle);
        if (!p) return;
        p.shots.slice(0, 2).forEach((s) => preloadImage(sized(s, w)));
      });
    }

    async select(dot) {
      const token = ++this.token;
      this.dots.forEach((d) => d.classList.toggle('is-active', d === dot));
      const p = await load(dot.dataset.handle);
      if (!p || token !== this.token) return;

      const w = this.width();
      const imgs = [...this.card.querySelectorAll('.card__media img')];
      const urls = imgs.map((_, i) => sized(p.shots[i] || p.shots[0], w));
      // Photo déjà préchargée → aucune attente ; sinon on attend 350 ms au plus avant d'afficher.
      await Promise.race([preloadImage(urls[0]), new Promise((r) => setTimeout(r, 350))]);
      if (token !== this.token) return;      // une autre pastille a été survolée entre-temps

      imgs.forEach((img, i) => {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = urls[i];
        if (i > 0) preloadImage(urls[i]);
      });
      this.card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        if (!a.closest('sana-card-swatches')) a.href = p.url;
      });
    }
  }
  customElements.define('sana-card-swatches', SanaCardSwatches);
})();
